import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { Linkedin } from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";
import { SISTER_BRANDS, LINKEDIN_LINK, FOUNDER_PHOTOS } from "../lib/data";

const CHAPTERS = [
  { num: "01", title: "A teacher first", body: "Deepankshi Saxena has been teaching since 2015 — thousands of students educated, from school kids to business owners and working women restarting their careers." },
  { num: "02", title: "A practitioner always", body: "She runs two creative studios — Design4You and Sanvalio — with 5,000+ brands served and 20,000+ projects delivered worldwide. Everything taught at the Academy is battle-tested on real client work." },
  { num: "03", title: "Officially registered, 2024", body: "Sanvalio Academy is a registered training company — your certificate and diploma carry institutional weight, not just a PDF." },
  { num: "04", title: "Passion over profit", body: "\"Teaching isn't my business, it's my passion. When a student gets their first client or first job — that's my real fee.\" — Deepankshi Saxena" },
];

export const Founder = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [40, -40]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (FOUNDER_PHOTOS.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % FOUNDER_PHOTOS.length), 3000);
    return () => clearInterval(t);
  }, []);

  return (
    <section id="founder" ref={ref} className="py-20 sm:py-28 lg:py-36 px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto" data-testid="founder-section">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-5">
          <motion.div style={{ y: imgY }} className="relative">
            <div className="absolute -inset-4 rounded-3xl bg-gold/15 blur-2xl" />
            <div className="relative overflow-hidden rounded-3xl border border-gold/30 shadow-[0_30px_80px_-20px_rgba(232,193,90,0.25)]" data-testid="founder-portrait-frame">
              <div className="relative aspect-[4/5] w-full">
                <AnimatePresence initial={false}>
                  <motion.img
                    key={FOUNDER_PHOTOS[idx]}
                    src={FOUNDER_PHOTOS[idx]}
                    alt="Deepankshi Saxena — Founder of Sanvalio Academy, Sanvalio and Design4You"
                    initial={{ opacity: 0, scale: 1.08, filter: "blur(18px)" }}
                    animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                    exit={{ opacity: 0, filter: "blur(18px)" }}
                    transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0 h-full w-full object-cover"
                    data-testid="founder-slider-image"
                  />
                </AnimatePresence>
              </div>
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-void via-void/60 to-transparent p-6 pt-16">
                <p className="font-display text-2xl text-champagne">Deepankshi Saxena</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-400">Founder · Sanvalio Academy, Sanvalio & Design4You</p>
                {FOUNDER_PHOTOS.length > 1 && (
                  <div className="mt-4 flex gap-1.5" data-testid="founder-slider-dots">
                    {FOUNDER_PHOTOS.map((p, i) => (
                      <button
                        key={p}
                        aria-label={`Photo ${i + 1}`}
                        onClick={() => setIdx(i)}
                        className={`h-1 rounded-full transition-all duration-500 ${i === idx ? "w-7 bg-gold" : "w-3 bg-white/30"}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
          <Reveal delay={0.15} className="mt-8">
            <div className="flex flex-wrap gap-2.5">
              {SISTER_BRANDS.map((b) => (
                <a
                  key={b.name}
                  data-testid={`founder-brand-${b.name.toLowerCase().replace(/\s+/g, "-")}`}
                  href={b.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full border border-white/15 px-4 py-2 text-xs text-neutral-300 hover:border-gold/60 hover:text-champagne transition-colors"
                >
                  {b.name} ↗
                </a>
              ))}
              <a
                data-testid="founder-linkedin-link"
                href={LINKEDIN_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-2 text-xs text-champagne hover:border-gold hover:bg-gold/20 transition-colors"
              >
                <Linkedin size={13} /> LinkedIn ↗
              </a>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <SectionHead
            overline="The founder"
            title={<>Teaching is her passion. <em className="text-gold-gradient italic">Results are her proof.</em></>}
          />
          <div className="mt-12 space-y-0">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.num} delay={i * 0.07}>
                <div className="group flex gap-6 border-t border-white/10 py-8 last:border-b" data-testid={`founder-chapter-${c.num}`}>
                  <span className="font-mono text-xs tracking-[0.25em] text-gold/70 pt-1.5">{c.num}</span>
                  <div>
                    <h3 className="font-display text-xl sm:text-2xl text-neutral-100 group-hover:text-champagne transition-colors duration-300">{c.title}</h3>
                    <p className="mt-3 max-w-xl text-sm sm:text-base font-light leading-relaxed text-neutral-400">{c.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
